import React, { useState, useEffect, useCallback } from 'react';
import { useRoom } from '../context/RoomContext';
import { useAuth } from '../context/AuthContext';
import Chat from './Chat';
import { messagesAPI, fichiersAPI } from '../services/api';
import socketService from '../services/socket';

const ChatRoom = ({ roomId, isOpen, onClose, mode = "panel", variant = "room", currentUserId }) => {
   const { socketId } = useRoom();
   const { user } = useAuth();
   const [messages, setMessages] = useState([]);
   const [isUploading, setIsUploading] = useState(false);
   const [files, setFiles] = useState([]);
   const [loading, setLoading] = useState(false);
   const userId = user?.id;

  // Charger l'historique des messages
  useEffect(() => {
    const fetchMessages = async () => {
      if (!roomId) return;
      setLoading(true);
      try {
        const response = await messagesAPI.getByRoom(roomId);
        // Formater les messages pour correspondre à l'interface attendue
        const formattedMessages = response.data.map(msg => ({
          id: msg.idmessage || msg._id || msg.id,
          message: msg.contenu || msg.message,
          senderId: msg.iduser || msg.senderId,
          senderName: msg.userName && msg.userPrenom ? `${msg.userPrenom} ${msg.userName}` : (msg.senderName || 'Anonyme'),
          timestamp: msg.dateEnvoie || msg.createdAt || msg.timestamp,
          type: msg.type || 'user'
        }));
        setMessages(formattedMessages);
      } catch (error) {
        console.error('Erreur chargement messages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [roomId]);

  const handleSendMessage = useCallback(async (text) => {
    if (!user || !roomId) return;

    // Créer un message temporaire avec ID temporaire
    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      message: text,
      senderId: user.id,
      senderName: user.name || user.email,
      timestamp: new Date().toISOString(),
      type: 'user',
      _isTemp: true
    };

    // Ajouter immédiatement à l'UI (optimistic update)
    setMessages(prev => [...prev, tempMessage]);

    try {
      // Envoyer via API REST pour persistance
      const response = await messagesAPI.send(roomId, {
        contenu: text,
      });

      // Le message sauvegardé par le serveur
      const realMessage = {
        id: response.data.idmessage || response.data.id,
        message: response.data.contenu || response.data.message,
        senderId: response.data.iduser || response.data.senderId,
        senderName: response.data.userName && response.data.userPrenom
          ? `${response.data.userPrenom} ${response.data.userName}`
          : (response.data.senderName || 'Anonyme'),
        timestamp: response.data.dateEnvoie || response.data.createdAt,
        type: 'user'
      };

      setMessages(prev => {
        // Si le message réel est déjà présent (ajouté via socket), on retire juste le temporaire
        if (prev.some(m => m.id === realMessage.id)) {
          return prev.filter(m => m.id !== tempId);
        }
        // Sinon, on remplace le temporaire par le réel
        return prev.map(m => m.id === tempId ? realMessage : m);
      });

      // Note: Le serveur vaémettre le message via WebSocket à tous les participants
      // y compris l'expéditeur. Le socket listener gérera la réception.
    } catch (error) {
      console.error('Erreur envoi message:', error);
      // Retirer le message temporaire en cas d'erreur
      setMessages(prev => prev.filter(m => m.id !== tempId));
    }
  }, [user, roomId]);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file || !user || !roomId) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('fichier', file);
      formData.append('userId', user.id);
      formData.append('userName', user.name || user.email);
      formData.append('roomId', roomId);
      formData.append('meetingId', roomId);

      const result = await fichiersAPI.uploadFile(formData);

      // Ajouter fichier à la liste locale
      setFiles(prev => [result.data, ...prev]);

      // Envoyer un message avec le fichier
      await handleSendMessage(`📎 ${file.name}`);
    } catch (error) {
      console.error('Erreur upload fichier:', error);
    } finally {
      setIsUploading(false);
      if (e.target) {
        e.target.value = '';
      }
    }
  };

  // Écouter les nouveaux messages en temps réel + filtre par salle
   const handleMessageUpdated = useCallback((data) => {
     const roomIdStr = String(roomId);
     const matchesId = data.id_meeting !== undefined && String(data.id_meeting) === roomIdStr;
     const matchesRoom = data.roomId !== undefined && String(data.roomId) === roomIdStr;
     if ((data.id_meeting !== undefined || data.roomId !== undefined) && !matchesId && !matchesRoom) return;

     setMessages(prev => prev.map(m => {
       const msgId = m.id || m.idmessage;
       if (String(msgId) === String(data.idmessage || data.id)) {
         return { ...m, message: data.contenu || data.message, edited: true };
       }
       return m;
     }));
   }, [roomId]);

   const handleMessageDeleted = useCallback((data) => {
     const roomIdStr = String(roomId);
     const matchesId = data.id_meeting !== undefined && String(data.id_meeting) === roomIdStr;
     const matchesRoom = data.roomId !== undefined && String(data.roomId) === roomIdStr;
     if ((data.id_meeting !== undefined || data.roomId !== undefined) && !matchesId && !matchesRoom) return;

     setMessages(prev => prev.filter(m => {
       const msgId = m.id || m.idmessage;
       return String(msgId) !== String(data.idmessage || data.id);
     }));
   }, [roomId]);

   useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket || !roomId) return;

    let seenIds = new Set();

    const handleNewMessage = (data) => {
      const roomIdStr = String(roomId);
      const matchesId = data.id_meeting !== undefined && String(data.id_meeting) === roomIdStr;
      const matchesRoom = data.roomId !== undefined && String(data.roomId) === roomIdStr;
      if ((data.id_meeting !== undefined || data.roomId !== undefined) && !matchesId && !matchesRoom) return;

      const msgId = data.idmessage || data.id;
       if (msgId && seenIds.has(msgId)) return;
       if (msgId) seenIds.add(msgId);

       const formattedMessage = {
         id:        data.idmessage || data.id,
         message:   data.contenu   || data.message,
         senderId:  data.iduser    || data.senderId,
         senderName: data.userName && data.userPrenom
           ? `${data.userPrenom} ${data.userName}`
           : (data.senderName || 'Anonyme'),
         timestamp: data.dateEnvoie || data.createdAt || data.timestamp || new Date().toISOString(),
         type:      data.type      || 'user',
       };

       setMessages(prev => {
         if (prev.some(m => m.id === formattedMessage.id)) return prev;
         return [...prev, formattedMessage];
       });
     };

     socket.on('chat-message', handleNewMessage);
     socket.on('message-updated', handleMessageUpdated);
     socket.on('message-deleted', handleMessageDeleted);

     return () => { 
       socket.off('chat-message', handleNewMessage); 
       socket.off('message-updated', handleMessageUpdated);
       socket.off('message-deleted', handleMessageDeleted);
     };
   }, [roomId, handleMessageUpdated, handleMessageDeleted]);

  if (loading) {
    return <div className="chat-loading">Chargement des messages...</div>;
  }

  const isPanelMode = mode === "panel";

return (
     <Chat
       messages={messages}
       onSendMessage={handleSendMessage}
        onEditMessage={async (messageId, nouveauContenu) => {
          await messagesAPI.update(messageId, nouveauContenu);
          setMessages(prev => prev.map(m => {
            const msgId = m.id || m.idmessage;
            if (String(msgId) === String(messageId)) {
              return { ...m, message: nouveauContenu, edited: true };
            }
            return m;
          }));
        }}
        onDeleteMessage={async (messageId) => {
          await messagesAPI.delete(messageId);
          setMessages(prev => prev.filter(m => (m.id || m.idmessage) !== messageId));
        }}
       currentUserId={userId}
       socketId={socketId}
       isOpen={isOpen}
       onClose={isPanelMode ? onClose : undefined}
       mode={mode}
       variant={variant}
       isUploading={isUploading}
       onFileSelect={handleFileSelect}
       files={files}
       roomId={roomId}
     />
   );
};

export default ChatRoom;
